import React, { useState, useEffect, useMemo, useContext } from "react";
import { Card, CardBody, Container, Spinner } from "reactstrap";
import { icfMasterColumns, checklistTypeColumns } from "../helpers/checklistconstant";
import { perPageData, showToastMessage } from "common/service/CommonService";
import { useParams, useNavigate } from "react-router-dom";
import { BreadcrumbContext } from "Layouts/Header";
import { LIBRARY_PATHS } from "Routes/paths";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import ICFAddEditModal from "../components/AddEditCheckListModal";
import AssignCategoryModal from "../components/AssignCategoryModal";
import { queryClient } from "api/queryClient";
import DeleteModal from "Components/Common/DeleteModal";
import Reason, { ReasonSubmitPayload } from "common/utilities/Reason/Reason";
import {
  CHECKLIST_CATEGORY_OPTIONS,
  Checklist_Type_OPTIONS,
  IcfChecklistAnswer_OPTIONS,
  loadCodelistsDetailId,
} from "api/modules/common.option";
import CommonSearch from "Components/Common/CommonSearch";
import { CheckListQuestion } from "../helpers/checklist.types";
import { useCheckListMasterService } from "modules/Library/Checklist/api/useChecklist";
import CommonButton from "Components/Common/CommonButton";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import FilePreviewModal from "modules/Study/Attachments/components/FilePreviewModal";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import ChecklistFilterModal, { ChecklistFilterValues } from "../components/ChecklistFilterModal";
import sf from "assets/scss/common/searchFilter.module.scss";

const CheckList: React.FC = () => {
  document.title = "CheckList | ZENTIXS VISTA";

  const navigate = useNavigate();
  const { type } = useParams();

  const [searchText, setSearchText] = useState<string | null>(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<CheckListQuestion | null>(null);

  const [viewingType, setViewingType] = useState<any>(null);

  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  // Modals state
  const [deleteModal, setDeleteModal] = useState(false);
  const [showDeleteReason, setShowDeleteReason] = useState(false);
  const [selectedRecord, setSelectedRecord] =
    useState<CheckListQuestion | null>(null);

  const [reasonModal, setReasonModal] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState<any>(null);

  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  // Filter state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<ChecklistFilterValues>({});
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isUiLoading, setIsUiLoading] = useState(false);

  // Checklist Master Service & Checklist Type Service
  const {
    SelectAllQuery,
    ChecklistTypeSelectAllQuery,
    InsertMutation,
    UpdateMutation,
    DeleteMutation,
    AssignCategoryMutation,
    UpdateCategoryMutation,
    UpdateDisplayOrderMutation,
  } = useCheckListMasterService({
      pageNumber: currentPageNumber,
      pageSize: perPageData,
      searchText: searchText,
      checklisttype: viewingType ? viewingType.label : null,
      checklistcategory: filters?.category || null,
      ispagination: viewingType ? true : false,
      enabled: !!viewingType,
      typeEnabled: !viewingType,
    });

  const [assignCategoryModalOpen, setAssignCategoryModalOpen] = useState(false);
  const [selectedTypeForCategory, setSelectedTypeForCategory] = useState<any>(null);
  const [selectedCategoryValue, setSelectedCategoryValue] = useState<any>(null);
  const [categoryReasonModal, setCategoryReasonModal] = useState(false);
  const [pendingCategoryUpdateData, setPendingCategoryUpdateData] = useState<any>(null);

  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [typeOptions, setTypeOptions] = useState<any[]>([]);

  useEffect(() => {
    loadCodelistsDetailId([
      "IcfChecklistAnswer_OPTIONS",
      "Checklist_Type_OPTIONS",
      "CHECKLIST_CATEGORY_OPTIONS",
    ]).then(() => {
      setTypeOptions([...Checklist_Type_OPTIONS]);
      setOptionsLoaded(true);
    });
  }, []);

  // Pre-populate type filter from path params when options are loaded
  useEffect(() => {
    if (type && typeOptions.length > 0) {
      const found = typeOptions.find(
        (opt) => String(opt.value) === String(type)
      );
      if (found) {
        setViewingType(found);
      }
    } else {
      setViewingType(null);
    }
  }, [type, typeOptions]);

  // Reset page when checklist type changes or search text changes
  useEffect(() => {
    setCurrentPageNumber(1);
  }, [viewingType, searchText]);

  // Set breadcrumbs dynamically
  useEffect(() => {
    if (!viewingType) {
      setBreadcrumbItems?.([]);
    } else {
      setBreadcrumbItems?.([
        { text: "Checklist", link: LIBRARY_PATHS.CHECKLIST_LIST },
        { text: viewingType.label },
      ]);
    }
  }, [viewingType, setBreadcrumbItems]);

  const { data: queryResponse, isLoading } = SelectAllQuery as {
    data?: { data?: CheckListQuestion[] };
    isLoading: boolean;
  };

  const data: CheckListQuestion[] = queryResponse?.data ?? [];

  // Server-side response for Checklist Types (when viewingType is null)
  const { data: checklistTypeQueryResponse, isLoading: isChecklistTypeLoading } =
    ChecklistTypeSelectAllQuery as {
      data?: { data?: any[] };
      isLoading: boolean;
    };

  const rawChecklistTypes: any[] = checklistTypeQueryResponse?.data ?? [];

  const typeTableData = useMemo(() => {
    return rawChecklistTypes.map((item: any) => ({
      ...item,
      value: item.id ?? item.codelistdetailid ?? item.codelistvalue ?? item.value,
      label: item.codelistname ?? item.label ?? "",
      checklistcategory: item.categoryid ?? item.checklistcategory ?? item.categoryvalue ?? null,
    }));
  }, [rawChecklistTypes]);

  const totalChecklistTypes = rawChecklistTypes[0]?.total_count ?? typeTableData.length;

  // Dynamic columns for checklist questions
  const columns = useMemo(() => {
    const cols = icfMasterColumns(IcfChecklistAnswer_OPTIONS, Checklist_Type_OPTIONS, CHECKLIST_CATEGORY_OPTIONS);
    if (viewingType) {
      return cols.filter((col) => col.key !== "checklisttypetext");
    }
    return cols;
  }, [viewingType, typeOptions]);

  const triggerUiLoading = () => {
    setIsUiLoading(true);
    setTimeout(() => {
      setIsUiLoading(false);
    }, 600);
  };

  const toggleFilter = () => setIsFilterOpen((prev) => !prev);

  const applyFilters = (values: ChecklistFilterValues) => {
    triggerUiLoading();
    if (!values.category) {
      setFilters({});
      setIsFilterApplied(false);
    } else {
      setFilters(values);
      setIsFilterApplied(true);
    }
    setCurrentPageNumber(1);
  };

  const clearFilters = () => {
    triggerUiLoading();
    setFilters({});
    setIsFilterApplied(false);
    setCurrentPageNumber(1);
  };

  const onSearch = (text: string | null) => {
    triggerUiLoading();
    setSearchText(text);
    setCurrentPageNumber(1);
  };

  const handleToggle = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) setEditData(null);
  };

  const handleEdit = (row: CheckListQuestion) => {
    setEditData(row);
    setModalOpen(true);
  };

  const handleDelete = (row: CheckListQuestion) => {
    setSelectedRecord(row);
    setDeleteModal(true);
  };

  const getAssignedCategoryForType = (row: any) => {
    if (!row) return null;
    return row.categoryid ?? row.checklistcategory ?? row.categoryvalue ?? null;
  };

  const handleAssignCategory = (row: any, existingCat: any = null) => {
    setSelectedTypeForCategory(row);
    setSelectedCategoryValue(existingCat);
    setAssignCategoryModalOpen(true);
  };

  const onSaveCategory = (payload: any) => {
    const isUpdate = selectedCategoryValue !== null && selectedCategoryValue !== undefined && selectedCategoryValue !== "";
    if (isUpdate) {
      setPendingCategoryUpdateData(payload);
      setAssignCategoryModalOpen(false);
      setCategoryReasonModal(true);
    } else {
      AssignCategoryMutation.mutate(payload, {
        onSuccess: (res: any) => {
          showToastMessage(
            "success",
            res?.message || "Category assigned successfully"
          );
          queryClient.invalidateQueries({
            queryKey: ["checklist-master-selectAll"],
          });
          queryClient.invalidateQueries({
            queryKey: ["checklist-type-selectAll"],
          });
          setAssignCategoryModalOpen(false);
          setSelectedTypeForCategory(null);
          setSelectedCategoryValue(null);
        },
        onError: (error: any) => {
          showToastMessage(
            "error",
            (error?.message ?? error) || "Failed to assign category"
          );
        },
      });
    }
  };

  const handleCategoryUpdateWithReason = (payload: ReasonSubmitPayload) => {
    const updatePayload = {
      ...pendingCategoryUpdateData,
      categoryupdatereason: payload.reason,
    };

    UpdateCategoryMutation.mutate(updatePayload, {
      onSuccess: (res: any) => {
        showToastMessage(
          "success",
          res?.message || "Category updated successfully"
        );
        queryClient.invalidateQueries({
          queryKey: ["checklist-master-selectAll"],
        });
        queryClient.invalidateQueries({
          queryKey: ["checklist-type-selectAll"],
        });
        setCategoryReasonModal(false);
        setPendingCategoryUpdateData(null);
        setSelectedTypeForCategory(null);
        setSelectedCategoryValue(null);
      },
      onError: (error: any) => {
        showToastMessage(
          "error",
          (error?.message ?? error) || "Failed to update category"
        );
      },
    });
  };

  const onDeleteConfirm = (payload: ReasonSubmitPayload) => {
    const deletePayload = {
      masterchecklistid: payload.id,
      updatereason: payload.reason,
    };

    DeleteMutation.mutate(deletePayload, {
      onSuccess: (res: any) => {
        showToastMessage(
          "success",
          res?.message || "Record deleted successfully",
        );
        queryClient.invalidateQueries({
          queryKey: ["checklist-master-selectAll"],
        });

        const totalRecords = data[0]?.total_count || 0;
        const lastPage = Math.ceil((totalRecords - 1) / perPageData);

        setCurrentPageNumber(
          Math.max(1, Math.min(currentPageNumber, lastPage)),
        );
        setDeleteModal(false);
        setShowDeleteReason(false);
        setSelectedRecord(null);
        setSearchText("");
      },
      onError: (error: any) => {
        showToastMessage("error", error.message || "Delete failed");
      },
    });
  };

  const onSave = (payload: any) => {
    if (editData) {
      // For update, open reason modal
      setPendingUpdateData(payload);
      setReasonModal(true);
    } else {
      // For insert
      InsertMutation.mutate(payload, {
        onSuccess: (res: any) => {
          showToastMessage(
            "success",
            res?.message || "Question saved successfully",
          );
          queryClient.invalidateQueries({
            queryKey: ["checklist-master-selectAll"],
          });
          setCurrentPageNumber(1);
          setSearchText("");
          handleToggle();
        },
        onError: (error: any) => {
          showToastMessage("error", (error?.message ?? error) || "Failed to save checklist");
        },
      });
    }
  };

  const handleUpdateWithReason = (payload: ReasonSubmitPayload) => {
    const updatePayload = {
      ...pendingUpdateData,
      masterchecklistid: editData?.masterchecklistid,
      updatereason: payload.reason,
    };

    UpdateMutation.mutate(updatePayload, {
      onSuccess: (res: any) => {
        showToastMessage(
          "success",
          res?.message || "Question updated successfully",
        );
        queryClient.invalidateQueries({
          queryKey: ["checklist-master-selectAll"],
        });
        handleToggle();
        setReasonModal(false);
        setPendingUpdateData(null);
        setCurrentPageNumber(1);
        setSearchText("");
      },
      onError: (error: any) => {
        showToastMessage("error", (error?.message ?? error) || "Failed to save checklist");
      },
    });
  };

  const questionList = useMemo(() => {
    const raw = SelectAllQuery?.data?.data?.data || SelectAllQuery?.data?.data || data || [];
    return raw.map((item: any, idx: number) => ({
      ...item,
      id: item.id ?? item.masterchecklistid ?? idx + 1,
    }));
  }, [SelectAllQuery?.data, data]);

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activeIndex = active.data.current?.sortable?.index;
    const overIndex = over.data.current?.sortable?.index;

    if (activeIndex == null || overIndex == null) return;

    const activeItem = questionList[activeIndex];
    const overItem = questionList[overIndex];

    const formatOrder = (orderVal: any, fallbackIdx: number) => {
      const val = orderVal ?? fallbackIdx + 1;
      const num = Number(val);
      if (!isNaN(num)) {
        return String(num).padStart(2, '0');
      }
      return String(val);
    };

    const activeDisplayOrder = formatOrder(activeItem.displayorder, activeIndex);
    const overDisplayOrder = formatOrder(overItem.displayorder, overIndex);

    const displayorderjson = [
      {
        masterchecklistid: activeItem.masterchecklistid,
        displayorder: overDisplayOrder,
      },
      {
        masterchecklistid: overItem.masterchecklistid,
        displayorder: activeDisplayOrder,
      },
    ];

    UpdateDisplayOrderMutation.mutate(
      { displayorderjson },
      {
        onSuccess: (response: any) => {
          showToastMessage("success", response?.message || "Order updated successfully");
          SelectAllQuery.refetch();
        },
        onError: (error: any) => {
          showToastMessage("error", error?.message ?? "Failed to update display order");
        },
      }
    );
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Checklist",
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Checklist_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  return (
    <div className="page-content">
      <Container fluid className="px-0">
        <Card className="table_wrapper">
          <CardBody>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className={sf.wrapper}>
                <CommonSearch onSearch={onSearch} searchText={searchText} />
                {!viewingType && (
                  <button
                    className={`${sf.filterBtn} ${isFilterApplied ? sf.active : ""}`}
                    onClick={toggleFilter}
                    title="Filter Checklist"
                  >
                    <i className="ri-filter-3-line" style={{ fontSize: 15 }} />
                    {isFilterApplied && <span className={sf.filterDot} />}
                  </button>
                )}
              </div>

              <div className="d-flex gap-2">
                {!viewingType && (
                  <CommonButton
                    module={PERMISSION_MODULES.CHECKLIST}
                    action={PERMISSION_ACTIONS.AUDIT}
                    color="primary"
                    size="sm"
                    onClick={onAuditClick}
                    disabled={AuditMutation.isPending}
                    outline
                  >
                    {AuditMutation.isPending ? (
                      <Spinner size="sm" className="me-2" />
                    ) : (
                      <i className="ri-history-line me-1"></i>
                    )}
                    Audit
                  </CommonButton>
                )}
                {viewingType && (
                  <CommonButton
                    color="primary"
                    size="sm"
                    outline
                    module={PERMISSION_MODULES.CHECKLIST}
                    action={PERMISSION_ACTIONS.ADD}
                    onClick={() => setModalOpen(true)}
                  >
                    <i className="ri-add-line align-middle me-1"></i> Add
                  </CommonButton>
                )}
              </div>
            </div>

            {viewingType ? (
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={questionList.map((item: any) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <CommonTableIndexWise
                    moduleName={PERMISSION_MODULES.CHECKLIST}
                    columns={columns}
                    data={questionList}
                    loading={isLoading || SelectAllQuery.isFetching || isUiLoading || UpdateDisplayOrderMutation.isPending}
                    pageSize={perPageData}
                    currentPage={currentPageNumber}
                    onPageChange={setCurrentPageNumber}
                    dragAndDrop
                    totalRecords={questionList[0]?.total_count || 0}
                    actions={{
                      onEdit: handleEdit,
                      onDelete: handleDelete,
                    }}
                  />
                </SortableContext>
              </DndContext>
            ) : (
              <CommonTableIndexWise
                moduleName={PERMISSION_MODULES.CHECKLIST}
                columns={checklistTypeColumns(
                  (row: any) => {
                    setViewingType(row);
                    navigate(`/checklist/list/${row.value}`);
                    setSearchText(null);
                  },
                  (row: any) => getAssignedCategoryForType(row),
                  CHECKLIST_CATEGORY_OPTIONS
                )}
                data={typeTableData}
                loading={!optionsLoaded || isChecklistTypeLoading || ChecklistTypeSelectAllQuery.isFetching || isUiLoading}
                pageSize={perPageData}
                currentPage={currentPageNumber}
                onPageChange={setCurrentPageNumber}
                totalRecords={totalChecklistTypes}
                actions={(row: any) => {
                  const existingCat = getAssignedCategoryForType(row);
                  if (existingCat !== null && existingCat !== undefined && existingCat !== null) {
                    return {
                      onUpdateCategory: (r: any) => handleAssignCategory(r, existingCat),
                    };
                  }
                  return {
                    onAssignCategory: (r: any) => handleAssignCategory(r, null),
                  };
                }}
              />
            )}
          </CardBody>
        </Card>
      </Container>

      <ICFAddEditModal
        isOpen={modalOpen}
        toggle={handleToggle}
        existingData={editData}
        preSelectedType={viewingType}
        onSave={onSave}
        isLoading={InsertMutation.isPending || UpdateMutation.isPending}
      />

      <DeleteModal
        isopen={deleteModal}
        recordId={selectedRecord?.masterchecklistid}
        recordName={selectedRecord?.question}
        toggle={() => setDeleteModal(!deleteModal)}
        onDeleteClick={onDeleteConfirm}
        isLoading={DeleteMutation.isPending}
        showDeleteReason={showDeleteReason}
        toggleDeleteReason={() => setShowDeleteReason(!showDeleteReason)}
      />

      <Reason
        isOpen={reasonModal}
        toggle={() => setReasonModal(!reasonModal)}
        action="Update"
        id={editData?.masterchecklistid}
        click={handleUpdateWithReason}
        isLoading={UpdateMutation.isPending}
      />

      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}

      <AssignCategoryModal
        isOpen={assignCategoryModalOpen}
        toggle={() => setAssignCategoryModalOpen(!assignCategoryModalOpen)}
        selectedType={selectedTypeForCategory}
        existingCategory={selectedCategoryValue}
        onSave={onSaveCategory}
        isLoading={AssignCategoryMutation.isPending || UpdateCategoryMutation.isPending}
      />

      <Reason
        isOpen={categoryReasonModal}
        toggle={() => setCategoryReasonModal(!categoryReasonModal)}
        action="Update"
        id={selectedTypeForCategory?.value}
        click={handleCategoryUpdateWithReason}
        isLoading={UpdateCategoryMutation.isPending}
      />

      <ChecklistFilterModal
        isOpen={isFilterOpen}
        toggle={toggleFilter}
        onApply={applyFilters}
        onClear={clearFilters}
        existingFilters={filters}
      />
    </div>
  );
};

export default CheckList;
